import React, { useState, useMemo, useEffect } from 'react';
import { Tabs, Tab, Table, Form, Button, Card, Row, Col, Badge, InputGroup, Pagination, Modal, Spinner } from 'react-bootstrap';
import { 
  FaBuilding, 
  FaMapMarkerAlt, 
  FaPlus, 
  FaSearch, 
  FaFilter, 
  FaEdit, 
  FaTrashAlt, 
  FaCheckCircle,
  FaGlobe,
  FaChartBar,
  FaHistory,
  FaCalendarAlt,
  FaIndustry,
  FaUsers
} from 'react-icons/fa';
import { toast } from 'react-toastify';

const Company = () => {
  const [key, setKey] = useState('list');
  const [searchTerm, setSearchTerm] = useState('');
  const [showEditModal, setShowEditModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [selectedCompany, setSelectedCompany] = useState(null);
  
  const [companies, setCompanies] = useState([
    { id: 1, name: 'TCS', location: 'Chennai', type: 'IT', size: '10,000+', founded: '1968' },
    { id: 2, name: 'Amazon', location: 'Bangalore', type: 'E-Commerce', size: '5,000+', founded: '1994' },
    { id: 3, name: 'Google', location: 'Hyderabad', type: 'Technology', size: '2,000+', founded: '1998' },
    { id: 4, name: 'Reliance', location: 'Mumbai', type: 'Conglomerate', size: '15,000+', founded: '1958' },
    { id: 5, name: 'Infosys', location: 'Pune', type: 'IT Services', size: '8,000+', founded: '1981' },
    { id: 6, name: 'Zomato', location: 'Gurugram', type: 'FoodTech', size: '1,000+', founded: '2008' },
    { id: 7, name: 'Flipkart', location: 'Bangalore', type: 'E-Commerce', size: '4,000+', founded: '2007' },
    { id: 8, name: 'HDFC Bank', location: 'Mumbai', type: 'Banking', size: '12,000+', founded: '1994' },
    { id: 9, name: 'Wipro', location: 'Bangalore', type: 'IT', size: '7,500+', founded: '1945' },
    { id: 10, name: 'Paytm', location: 'Noida', type: 'FinTech', size: '3,000+', founded: '2010' },
  ]);

  const [formData, setFormData] = useState({
    name: '',
    location: '',
    type: '',
    size: '100-500',
    founded: ''
  });

  // Pagination State
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 6;

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleAddCompany = (e) => {
    e.preventDefault();
    if (!formData.name || !formData.location || !formData.type) {
      toast.warn('Please fill in all required fields.');
      return;
    }

    const newCompany = {
      id: companies.length + 1,
      ...formData
    };

    setCompanies((prev) => [newCompany, ...prev]);
    setFormData({ name: '', location: '', type: '', size: '100-500', founded: '' });
    setKey('list');
    toast.success('New company registered successfully!');
  };

  const handleDelete = () => {
    setCompanies(companies.filter(c => c.id !== selectedCompany.id));
    setShowDeleteModal(false);
    toast.success(`${selectedCompany.name} partnership has been terminated.`);
  };

  const handleEditSubmit = (e) => {
    e.preventDefault();
    setCompanies(companies.map(c => c.id === selectedCompany.id ? selectedCompany : c));
    setShowEditModal(false);
    toast.success('Company profile updated successfully.');
  };

  const filteredCompanies = useMemo(() => {
    return companies.filter(c => 
      c.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      c.location.toLowerCase().includes(searchTerm.toLowerCase()) ||
      c.type.toLowerCase().includes(searchTerm.toLowerCase())
    );
  }, [companies, searchTerm]);

  // Pagination Logic (Sliding Window)
  const totalPages = Math.ceil(filteredCompanies.length / itemsPerPage);
  const indexOfLastItem = currentPage * itemsPerPage;
  const indexOfFirstItem = indexOfLastItem - itemsPerPage;
  const currentItems = filteredCompanies.slice(indexOfFirstItem, indexOfLastItem);

  const renderPaginationItems = () => {
    const items = [];
    if (totalPages <= 5) {
      for (let i = 1; i <= totalPages; i++) {
        items.push(
          <Pagination.Item key={i} active={i === currentPage} onClick={() => setCurrentPage(i)}>
            {i}
          </Pagination.Item>
        );
      }
      return items;
    }

    let start = currentPage - 1;
    let end = currentPage + 1;

    if (start <= 1) {
      start = 1;
      end = 3;
    } else if (end >= totalPages) {
      end = totalPages;
      start = totalPages - 2;
    }

    if (start > 1) {
      items.push(<Pagination.Ellipsis key="start-el" disabled />);
    }

    for (let i = start; i <= end; i++) {
      if (i < totalPages) {
        items.push(
          <Pagination.Item key={i} active={i === currentPage} onClick={() => setCurrentPage(i)}>
            {i}
          </Pagination.Item>
        );
      }
    }

    if (end < totalPages - 1) {
      items.push(<Pagination.Ellipsis key="end-el" disabled />);
    }

    items.push(
      <Pagination.Item key={totalPages} active={currentPage === totalPages} onClick={() => setCurrentPage(totalPages)}>
        {totalPages}
      </Pagination.Item>
    );

    return items;
  };

  const getTypeBadgeColor = (type) => {
    const t = type.toLowerCase();
    if (t.includes('it')) return 'primary';
    if (t.includes('tech')) return 'success';
    if (t.includes('fin')) return 'warning';
    if (t.includes('commerce')) return 'info';
    return 'secondary';
  };

  const stats = [
    { title: 'Total Partners', count: companies.length, icon: <FaBuilding />, color: '#6366f1' },
    { title: 'Top Locations', count: 12, icon: <FaMapMarkerAlt />, color: '#10b981' },
    { title: 'Industry Types', count: 8, icon: <FaIndustry />, color: '#f59e0b' },
    { title: 'New Listings', count: 3, icon: <FaCalendarAlt />, color: '#ec4899' },
  ];

  return (
    <div className="company-mgmt-root p-4">
      {/* Header */}
      <div className="d-flex justify-content-between align-items-center mb-4">
        <div>
          <h2 className="fw-bold text-dark mb-1">Corporate Relations</h2>
          <p className="text-muted small mb-0">Manage global hiring partners and organizational profiles</p>
        </div>
        <Button 
          variant="primary" 
          onClick={() => setKey('add')}
          className="btn-modern shadow-sm"
        >
          <FaPlus className="me-2" /> Register Company
        </Button>
      </div>

      {/* Metrics Row */}
      <Row className="g-3 mb-4">
        {stats.map((stat, i) => (
          <Col md={3} key={i}>
            <Card className="border-0 shadow-sm metric-card p-3">
              <div className="d-flex align-items-center">
                <div className="metric-icon" style={{ color: stat.color, background: `${stat.color}15` }}>
                  {stat.icon}
                </div>
                <div className="ms-3">
                  <div className="small text-muted fw-bold text-uppercase tracking-wider" style={{ fontSize: '0.65rem' }}>{stat.title}</div>
                  <h4 className="fw-bold mb-0">{stat.count}</h4>
                </div>
              </div>
            </Card>
          </Col>
        ))}
      </Row>

      <Card className="border-0 shadow-sm rounded-3 overflow-hidden">
        <div className="p-3 bg-white border-bottom d-flex justify-content-between align-items-center flex-wrap gap-3">
          <Tabs 
            activeKey={key} 
            onSelect={(k) => { setKey(k); setCurrentPage(1); }}
            className="modern-tabs border-0"
          >
            <Tab eventKey="list" title="Partner Directory" />
            <Tab eventKey="add" title="Registration Form" />
          </Tabs>

          {key === 'list' && (
            <div className="d-flex gap-2">
              <InputGroup className="search-group" style={{ maxWidth: '300px' }}>
                <InputGroup.Text className="bg-light border-0">
                  <FaSearch className="text-muted small" />
                </InputGroup.Text>
                <Form.Control
                  className="bg-light border-0 small shadow-none"
                  placeholder="Search name, location..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                />
              </InputGroup>
              <Button variant="light" className="border btn-icon shadow-none"><FaFilter /></Button>
            </div>
          )}
        </div>

        <div className="tab-content">
          {key === 'list' ? (
            <>
              <div className="table-responsive">
                <Table hover className="modern-table mb-0 align-middle">
                  <thead>
                    <tr>
                      <th className="ps-4">Organization</th>
                      <th>Core Industry</th>
                      <th>Primary Hub</th>
                      <th>Scale</th>
                      <th>Since</th>
                      <th className="text-end pe-4">Command</th>
                    </tr>
                  </thead>
                  <tbody>
                    {currentItems.length === 0 ? (
                      <tr>
                        <td colSpan="6" className="text-center py-5 text-muted small">No organizations match your current view.</td>
                      </tr>
                    ) : (
                      currentItems.map((company) => (
                        <tr key={company.id} className="company-row">
                          <td className="ps-4">
                            <div className="d-flex align-items-center">
                              <div className="company-logo-badge me-3">
                                {company.name.charAt(0)}
                              </div>
                              <div>
                                <div className="fw-bold text-dark mb-0">{company.name}</div>
                                <div className="x-small text-muted">Org ID: #C00{company.id}</div>
                              </div>
                            </div>
                          </td>
                          <td>
                            <Badge bg={getTypeBadgeColor(company.type)} className="px-2 py-1 fw-bold x-small rounded-pill">
                              {company.type.toUpperCase()}
                            </Badge>
                          </td>
                          <td>
                            <div className="small text-muted d-flex align-items-center">
                              <FaMapMarkerAlt className="me-2 text-danger x-small" /> {company.location}
                            </div>
                          </td>
                          <td>
                            <div className="small fw-bold text-dark d-flex align-items-center">
                              <FaUsers className="me-2 text-primary x-small" /> {company.size}
                            </div>
                          </td>
                          <td>
                            <div className="x-small text-muted fw-bold">{company.founded}</div>
                          </td>
                          <td className="text-end pe-4">
                            <div className="d-flex justify-content-end gap-2">
                              <button className="action-circle-btn edit" onClick={() => { setSelectedCompany(company); setShowEditModal(true); }}><FaEdit /></button>
                              <button className="action-circle-btn delete" onClick={() => { setSelectedCompany(company); setShowDeleteModal(true); }}><FaTrashAlt /></button>
                            </div>
                          </td>
                        </tr>
                      ))
                    )}
                  </tbody>
                </Table>
              </div>

              {totalPages > 1 && (
                <div className="p-3 bg-light border-top d-flex justify-content-between align-items-center">
                  <div className="small text-muted">
                    Showing {indexOfFirstItem + 1} to {Math.min(indexOfLastItem, filteredCompanies.length)} of {filteredCompanies.length} entities
                  </div>
                  <Pagination className="mb-0 modern-pagination">
                    <Pagination.Prev onClick={() => setCurrentPage(Math.max(1, currentPage - 1))} disabled={currentPage === 1} />
                    {renderPaginationItems()}
                    <Pagination.Next onClick={() => setCurrentPage(Math.min(totalPages, currentPage + 1))} disabled={currentPage === totalPages} />
                  </Pagination>
                </div>
              )}
            </>
          ) : (
            <div className="p-4 bg-white">
              <Form onSubmit={handleAddCompany}>
                <Row className="g-4">
                  <Col md={8}>
                    <div className="section-label mb-3">Identity & Hub</div>
                    <Row className="g-3">
                      <Col md={6}>
                        <Form.Group>
                          <Form.Label className="x-small fw-bold text-muted text-uppercase">Legal Entity Name</Form.Label>
                          <Form.Control 
                            className="bg-light border-0 shadow-none" 
                            required 
                            placeholder="e.g. Microsoft India"
                            name="name"
                            value={formData.name}
                            onChange={handleInputChange}
                          />
                        </Form.Group>
                      </Col>
                      <Col md={6}>
                        <Form.Group>
                          <Form.Label className="x-small fw-bold text-muted text-uppercase">Headquarters Location</Form.Label>
                          <Form.Control 
                            className="bg-light border-0 shadow-none" 
                            required 
                            placeholder="e.g. Hyderabad"
                            name="location"
                            value={formData.location}
                            onChange={handleInputChange}
                          />
                        </Form.Group>
                      </Col>
                      <Col md={12}>
                        <Form.Group>
                          <Form.Label className="x-small fw-bold text-muted text-uppercase">Industry Description</Form.Label>
                          <Form.Control 
                            as="textarea" 
                            rows={6} 
                            className="bg-light border-0 shadow-none" 
                            placeholder="Primary services and market position..."
                          />
                        </Form.Group>
                      </Col>
                    </Row>
                  </Col>

                  <Col md={4}>
                    <div className="section-label mb-3">Operational Meta</div>
                    <div className="p-3 border rounded-3 bg-light-subtle">
                      <Row className="g-3">
                        <Col xs={12}>
                          <Form.Group>
                            <Form.Label className="x-small fw-bold text-muted text-uppercase">Company Type</Form.Label>
                            <Form.Control 
                              className="bg-white border shadow-none" 
                              required 
                              placeholder="e.g. IT, Banking"
                              name="type"
                              value={formData.type}
                              onChange={handleInputChange}
                            />
                          </Form.Group>
                        </Col>
                        <Col xs={12}>
                          <Form.Group>
                            <Form.Label className="x-small fw-bold text-muted text-uppercase">Headcount Range</Form.Label>
                            <Form.Select 
                              className="bg-white border shadow-none"
                              name="size"
                              value={formData.size}
                              onChange={handleInputChange}
                            >
                              <option value="1-10">1-10 (Startup)</option>
                              <option value="11-50">11-50 (Small)</option>
                              <option value="51-500">51-500 (Mid-size)</option>
                              <option value="500-5000">500-5000 (Large)</option>
                              <option value="5000+">5000+ (Enterprise)</option>
                            </Form.Select>
                          </Form.Group>
                        </Col>
                        <Col xs={12}>
                          <Form.Group>
                            <Form.Label className="x-small fw-bold text-muted text-uppercase">Founding Year</Form.Label>
                            <Form.Control 
                              className="bg-white border shadow-none" 
                              placeholder="YYYY"
                              name="founded"
                              value={formData.founded}
                              onChange={handleInputChange}
                            />
                          </Form.Group>
                        </Col>
                      </Row>
                    </div>

                    <div className="mt-4 d-grid gap-2">
                      <Button variant="primary" type="submit" className="fw-bold py-2 shadow-sm">
                        <FaCheckCircle className="me-2" /> REGISTER ENTITY
                      </Button>
                      <Button variant="outline-secondary" className="fw-bold border-0" onClick={() => setKey('list')}>CANCEL</Button>
                    </div>
                  </Col>
                </Row>
              </Form>
            </div>
          )}
        </div>
      </Card>

      {/* Edit Modal */}
      <Modal show={showEditModal} onHide={() => setShowEditModal(false)} centered className="modern-modal">
        <Modal.Header closeButton className="border-0 pb-0">
          <Modal.Title className="fw-bold">Modify Company Data</Modal.Title>
        </Modal.Header>
        <Form onSubmit={handleEditSubmit}>
          <Modal.Body className="p-4">
            {selectedCompany && (
              <Row className="g-3">
                <Col md={12}>
                  <Form.Group>
                    <Form.Label className="x-small fw-bold text-muted">COMPANY NAME</Form.Label>
                    <Form.Control 
                      className="bg-light border-0"
                      value={selectedCompany.name}
                      onChange={(e) => setSelectedCompany({...selectedCompany, name: e.target.value})}
                    />
                  </Form.Group>
                </Col>
                <Col md={6}>
                  <Form.Group>
                    <Form.Label className="x-small fw-bold text-muted">LOCATION</Form.Label>
                    <Form.Control 
                      className="bg-light border-0"
                      value={selectedCompany.location}
                      onChange={(e) => setSelectedCompany({...selectedCompany, location: e.target.value})}
                    />
                  </Form.Group>
                </Col>
                <Col md={6}>
                  <Form.Group>
                    <Form.Label className="x-small fw-bold text-muted">TYPE</Form.Label>
                    <Form.Control 
                      className="bg-light border-0"
                      value={selectedCompany.type}
                      onChange={(e) => setSelectedCompany({...selectedCompany, type: e.target.value})}
                    />
                  </Form.Group>
                </Col>
              </Row>
            )}
          </Modal.Body>
          <Modal.Footer className="border-0 pt-0">
            <Button variant="light" onClick={() => setShowEditModal(false)}>Discard</Button>
            <Button variant="primary" type="submit" className="fw-bold">Apply Changes</Button>
          </Modal.Footer>
        </Form>
      </Modal>

      {/* Delete Modal */}
      <Modal show={showDeleteModal} onHide={() => setShowDeleteModal(false)} centered className="modern-modal danger">
        <Modal.Body className="p-4 text-center">
          <div className="delete-icon-wrapper mb-3">
            <FaTrashAlt />
          </div>
          <h5 className="fw-bold">Terminate Partnership?</h5>
          <p className="text-muted small">You are about to remove <strong>{selectedCompany?.name}</strong>. This will unlink all associated jobs and applications.</p>
          <div className="d-grid gap-2">
            <Button variant="danger" className="fw-bold" onClick={handleDelete}>CONFIRM DELETION</Button>
            <Button variant="light" onClick={() => setShowDeleteModal(false)}>Cancel</Button>
          </div>
        </Modal.Body>
      </Modal>

      <style>{`
        .company-mgmt-root {
          background: #f8fafc;
          min-height: 100vh;
          font-family: 'Inter', system-ui, -apple-system, sans-serif;
        }

        .metric-card {
          border-radius: 12px;
          border: 1px solid #e2e8f0 !important;
        }

        .metric-icon {
          width: 48px;
          height: 48px;
          border-radius: 12px;
          display: flex;
          align-items: center;
          justify-content: center;
          font-size: 1.2rem;
        }

        .btn-modern {
          border-radius: 8px;
          padding: 0.6rem 1.25rem;
          font-weight: 600;
          font-size: 0.85rem;
        }

        .modern-tabs .nav-link {
          color: #64748b;
          border: 0;
          font-size: 0.85rem;
          font-weight: 600;
          padding: 1rem 1.5rem;
          position: relative;
        }

        .modern-tabs .nav-link.active {
          color: #6366f1;
          background: transparent;
        }

        .modern-tabs .nav-link.active::after {
          content: '';
          position: absolute;
          bottom: 0;
          left: 0;
          right: 0;
          height: 3px;
          background: #6366f1;
        }

        .modern-table thead th {
          background: #f8fafc;
          font-size: 0.7rem;
          text-transform: uppercase;
          letter-spacing: 0.05em;
          color: #94a3b8;
          border-bottom: 1px solid #e2e8f0;
          font-weight: 700;
          padding: 1rem !important;
        }

        .company-row:hover {
          background-color: #f1f5f9;
        }

        .company-logo-badge {
          width: 40px;
          height: 40px;
          background: #e0e7ff;
          color: #4f46e5;
          border-radius: 10px;
          display: flex;
          align-items: center;
          justify-content: center;
          font-weight: 800;
          font-size: 1.2rem;
        }

        .action-circle-btn {
          width: 32px;
          height: 32px;
          border-radius: 50%;
          border: 0;
          display: flex;
          align-items: center;
          justify-content: center;
          font-size: 0.8rem;
          transition: all 0.2s;
        }

        .action-circle-btn.edit { background: #e0e7ff; color: #4f46e5; }
        .action-circle-btn.delete { background: #fee2e2; color: #ef4444; }
        .action-circle-btn:hover { transform: scale(1.1); }

        .section-label {
          font-size: 0.7rem;
          font-weight: 800;
          text-transform: uppercase;
          letter-spacing: 0.1em;
          color: #94a3b8;
        }

        .modern-pagination .page-link {
          border: 0;
          background: transparent;
          color: #64748b;
          font-size: 0.85rem;
          font-weight: 600;
          margin: 0 2px;
          border-radius: 6px;
        }

        .modern-pagination .page-item.active .page-link {
          background: #6366f1;
          color: white;
        }

        .delete-icon-wrapper {
          width: 60px;
          height: 60px;
          background: #fee2e2;
          color: #ef4444;
          border-radius: 50%;
          display: flex;
          align-items: center;
          justify-content: center;
          margin: 0 auto;
          font-size: 1.5rem;
        }

        .modern-modal .modal-content {
          border-radius: 1.5rem;
          border: 0;
        }

        .btn-icon {
          width: 38px;
          height: 38px;
          display: flex;
          align-items: center;
          justify-content: center;
          padding: 0;
        }

        .x-small { font-size: 0.75rem; }
      `}</style>
    </div>
  );
};

export default Company;